import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useFormik } from 'formik';
//@ts-ignore
import ReCAPTCHA from 'react-google-recaptcha';

import api, { continueWithEmail } from 'utils/api';
import { getAuth, passwordSignedIn, useAuth } from 'utils/auth';
import useSsoLabel from 'utils/useSsoLabel';

import Container from 'components/common/Container';
import TextInput from 'components/common/TextInput';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CustomLink from 'components/common/CustomLink';
// =============================================================================
export default function Login() {
  const auth = useAuth();
  const passwordInput = useRef<HTMLInputElement>(null);
  const [passwordStep, setPasswordStep] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();
  const [captchaDone, setCaptchaDone] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    onSubmit: async (values) => {
      if (!values.email.trim() || (passwordStep && !values.password)) {
        setSnackbar('Fields cannot be empty!');
        return;
      }

      try {
        const email = values.email.trim().toLowerCase();
        if (!passwordStep) {
          if (await continueWithEmail(email)) {
            await formik.setFieldValue('email', email);
            setPasswordStep(true);
          } else {
            setRedirecting(true);
          }
          return;
        }
        const res = await api.post('/public/login', {
          email,
          password: values.password
        });
        passwordSignedIn(res.data);
        window.myAntsTag?.(
          'SET',
          'userId',
          String(getAuth().user?.userId ?? '')
        );
        window.myAntsTag?.('EVENT', 'LOGIN');
      } catch (err: any) {
        setSnackbar(
          passwordStep
            ? err.response?.data?.message ||
                'Unable to sign in. Please check your password and try again.'
            : 'Unable to check your email. Please try again.'
        );
      }
    }
  });

  // Focus after the email field is disabled and the password step has rendered.
  useEffect(() => {
    if (passwordStep) passwordInput.current?.focus();
  }, [passwordStep]);

  const sso = useSsoLabel(formik.values.email, !passwordStep);

  useEffect(() => {
    if (auth.user) navigate('/data', { replace: true });
  }, [auth.user, navigate]);

  return (
    <div>
      <Container style={{ marginBottom: 48, marginTop: 48 }}>
        <Typography variant='h2' color='blue.900'>
          Login
        </Typography>

        <form
          onSubmit={formik.handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '40px 0'
          }}
        >
          <TextInput
            variant='outlined'
            id='email'
            name='email'
            type='email'
            disabled={passwordStep}
            label='Email'
            placeholder='email@domain.com'
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            containerStyle={{ width: '100%', maxWidth: 400 }}
          />
          {passwordStep && (
            <TextInput
              variant='outlined'
              id='password'
              inputRef={passwordInput}
              name='password'
              label='Password'
              type='password'
              placeholder='**********'
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              containerStyle={{ width: '100%', maxWidth: 400 }}
            />
          )}
          {passwordStep && (
            <Button
              disabled={formik.isSubmitting}
              onClick={() => {
                setPasswordStep(false);
                formik.setFieldValue('password', '');
              }}
            >
              Sign in with a different email address
            </Button>
          )}

          <div style={{ height: 16 }} />
          {!!import.meta.env.RECAPTCHA_SECRET_KEY && (
            <ReCAPTCHA
              sitekey={import.meta.env.RECAPTCHA_CLIENT_KEY}
              onChange={() => setCaptchaDone(true)}
            />
          )}
          <div style={{ height: 16 }} />

          <Button
            type='submit'
            variant='primary'
            color='dark'
            disabled={
              formik.isSubmitting ||
              redirecting ||
              (!captchaDone && !!import.meta.env.RECAPTCHA_SECRET_KEY)
            }
          >
            {redirecting
              ? 'Redirecting…'
              : passwordStep
                ? 'Login'
                : sso
                  ? 'Continue with SSO'
                  : 'Continue'}
          </Button>
          <Typography
            color='blue.900'
            component={'div'}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0 8px',
              flexWrap: 'wrap',
              marginTop: 1,
              fontWeight: '600'
            }}
          >
            Don’t have an account?{' '}
            <CustomLink to='/signup' color='blue.600'>
              Sign up
            </CustomLink>
          </Typography>
          <CustomLink to='/resetPassword' color='blue.600'>
            Forgot password?
          </CustomLink>
        </form>

        <div style={{ height: 40 }} />
      </Container>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          sx={{
            width: '100%',
            maxWidth: 300
          }}
          onClose={() => setSnackbar('')}
          variant={'filled'}
          severity={'error'}
        >
          {snackbar}
        </Alert>
      </Snackbar>
    </div>
  );
}
